import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Globe, Pause, Play, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * `/admin/tenants` — every buyer with their own auto-provisioned white-label instance.
 * Admin can suspend / reactivate.
 */
export default function AdminTenants() {
  const [rows, setRows] = useState(null);
  const [root, setRoot] = useState("dynamictax.online");

  const load = () => {
    api.get("/admin/tenants").then(r => {
      setRows(r.data.tenants || []);
      if (r.data.root_domain) setRoot(r.data.root_domain);
    }).catch(() => toast.error("Could not load tenants"));
  };
  useEffect(load, []);

  const patch = async (tid, status) => {
    try {
      await api.patch(`/admin/tenants/${tid}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("URL copied"); };

  if (!rows) return <Layout><div className="p-10 text-muted-foreground">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-12" data-testid="admin-tenants-page">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft size={14} /> Back to admin</Link>
        <div className="flex items-center gap-3 mb-2">
          <Globe size={22} className="text-primary" />
          <div className="label-caps">White-label buyers</div>
        </div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-2">Tenants.</h1>
        <p className="text-muted-foreground mb-8">
          {rows.length} branded instance{rows.length === 1 ? "" : "s"} live under <code>*.{root}</code>.
        </p>

        {rows.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">No white-label tenants yet.</Card>
        ) : (
          <div className="space-y-3" data-testid="tenant-rows">
            {rows.map(t => {
              const url = `https://${t.subdomain}.${root}`;
              const active = t.status === "active";
              return (
                <Card key={t.id} className="p-5" data-testid={`tenant-row-${t.id}`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div
                      className="h-12 w-12 shrink-0 rounded-md grid place-items-center text-white font-serif text-xl"
                      style={{ backgroundColor: t.primary_color || "#2E4FB8" }}
                    >
                      {t.logo_url
                        ? <img src={t.logo_url} alt="" className="h-full w-full object-cover rounded-md" />
                        : (t.brand_name || "?")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                          {active ? "Active" : t.status || "—"}
                        </span>
                        <span className="text-[11px] text-muted-foreground">Since {new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="font-serif text-xl truncate">{t.brand_name}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <a href={url} target="_blank" rel="noreferrer" className="font-mono underline text-primary hover:text-primary/80 truncate max-w-md">{url}</a>
                        <button onClick={() => copy(url)} className="text-muted-foreground hover:text-foreground" title="Copy URL"><Copy size={11} /></button>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Admin: <code>{t.admin_email}</code>
                        {t.contact_phone && <> · {t.contact_phone}</>}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" asChild data-testid={`tenant-visit-${t.id}`}>
                        <a href={url} target="_blank" rel="noreferrer"><ExternalLink size={12} className="mr-1" /> Visit</a>
                      </Button>
                      {active ? (
                        <Button size="sm" variant="outline" onClick={() => patch(t.id, "suspended")} data-testid={`tenant-suspend-${t.id}`}>
                          <Pause size={12} className="mr-1" /> Suspend
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => patch(t.id, "active")} data-testid={`tenant-reactivate-${t.id}`}>
                          <Play size={12} className="mr-1" /> Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
